import { Container } from "aurelia-framework";
import { computed, defineComponent, PropType, ref } from "vue";
import { UserManager } from "oidc-client";
import { mapActions, mapState } from "pinia";
import moment from "moment";
import { useApp } from "../../../../App/useApp";
import { useCallStore } from "../../../store/useCallStore";
import { FamilyPortalService } from "../../../../../services/family-portal-service";
import { SignalRService } from "../../../../../services/signal-r-service";
import { MessageService } from "../../../../../services/message-service";
import {
    IConversationGroup,
    IConversationGroups,
    ICreateThreadRequest,
    ICreateThreadResponse,
    IFamilyContacts,
    IConversationCall,
    IFPStartCallRequest,
    IGetThreadConversationsByIdResponse,
    INewThread
} from "../../../../../resources-vue/vue-interfaces/messaging-service/i-family-portal";
import { IConversationUser } from "../../../../../resources-vue/vue-interfaces/i-message";
import { IPromptOptions } from "../../../../../resources-vue/vue-interfaces/i-prompt";
import { IMessageUser, RoomDetails } from "../../../../../resources-vue/vue-interfaces/messaging-service/i-message";
import {
    ICallStatusChange,
    IOutgoingCallProps,
    ICallControls
} from "../../../../../resources-vue/vue-interfaces/messaging-service/i-notification";
import { Prompt } from "../../../../../resources/dialogs/prompt/prompt";
import eventBus from "../../../../../utilities/eventBus";
import { jitsiRoomHandler } from "../../../../../utilities/jitsiRoomHandler";
import {
    ConversationRoomTypeEnum,
    ConversationTypeEnum,
    FPTabsEnum,
    GroupType,
    GroupTypeEnum,
    jitsiConferenceStatus,
    ParentPermissionEnum,
    PermissionActionEnum
} from "../../../../../enums/enums";

import {
    formatDisplayUsers,
    formatDateTimeToLocal,
    formatUserInitials
} from "../../../../../common/vue-helpers/modifiers/format-message";
import { getSolutionClassIndex } from "../../../../../common/vue-helpers/solution-color-index";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import MessageInputHandler from "../../../../../resources-vue/vue-custom-elements/MessagingService/MessageInputHandler/MessageInputHandler.vue";
import FPThreadsCard from "../FPThreadsCard/FPThreadsCard.vue";

export default defineComponent({
    components: {
        FPThreadsCard,
        MessageInputHandler
    },

    computed: {
        ...mapState(useApp, ["loggedInUserDetails"]),
        ...mapState(useCallStore, ["currentCallDetails", "showCallHeader", "outgoingCallInfo"]),

        allPagesLoaded(): boolean {
            return !!this.pageCount && this.pageNumber > this.pageCount;
        },
        showJoinBtn() {
            return (
                this.showCallHeader.convId == this.selectedConversationId &&
                this.showCallHeader.show &&
                this.currentCallDetails.convId !== this.selectedConversationId
            );
        },
        isCallOngoing() {
            return this.showCallHeader.show && this.showCallHeader.convId === this.selectedConversationId;
        },
        selectedContact(): IConversationUser {
            return {
                firstName: this.currentContact?.firstName,
                lastName: this.currentContact?.lastName,
                userId: this.currentContact?.loginId
            };
        },
        canEditPatient(): boolean {
            return this._permissionManager.checkPermissionByProduct(
                this.getPermissionEnum(this.selectedConversation?.conversationGroupType),
                PermissionActionEnum.canEdit,
                this.selectedConversation?.application,
                this.selectedConversation?.agencyId
            );
        },
        hasThreads(): boolean {
            return this.conversationThreadsInfo?.threads?.length > 0;
        }
    },

    props: {
        conversationThreadsInfo: {
            type: Object as PropType<IGetThreadConversationsByIdResponse>,
            default: null
        },
        selectedConversationId: {
            type: String,
            default: null
        },
        selectedConversation: {
            type: Object as PropType<IConversationGroup>,
            default: null
        },
        isLoading: {
            type: Boolean,
            default: true
        },
        isLoadingThreads: {
            type: Boolean,
            default: true
        },
        familyContacts: {
            type: Array as PropType<IFamilyContacts[]>,
            default: null
        }
    },

    data() {
        return {
            isLoading: false as boolean,
            expandedIndex: -1,
            _familyPortalService: null as FamilyPortalService,
            _messageService: null as MessageService,
            _permissionManager: null as PermissionManager,
            conversationGroupsCount: null as IConversationGroups,
            groupConversations: null as IConversationGroup[],
            userOs: "" as string,
            term: "" as string,
            pageNumber: 1 as number,
            pageLength: 20 as number,
            pageCount: null as number,
            hasError: false,
            formatDisplayUsers,
            GroupType,
            openCompose: false as boolean,
            newThread: {} as INewThread,
            subject: "" as string,
            activeTab: FPTabsEnum.Thread as FPTabsEnum,
            FPTabsEnum,
            getSolutionClassIndex,
            formatUserInitials,
            dialogOpened: false as boolean,
            signalRService: null as SignalRService,
            callStatus: null as ICallStatusChange,
            callDuration: null as string,
            axxessUserName: "Axxess",
            intervalId: null as NodeJS.Timeout,
            currentUserDetails: null,
            callControls: null as ICallControls,
            disableCallBtns: false as boolean,
            selectedConversationType: ConversationRoomTypeEnum.AudioCall,
            dialogRef: ref(null),
            conversationRoomDetails: null as RoomDetails,
            ConversationRoomTypeEnum: ConversationRoomTypeEnum,
            user: null as IConversationUser,
            currentContact: null as IFamilyContacts,
            selectedCallHistory: [] as IConversationCall[],
            formatDateTimeToLocal
        };
    },

    watch: {
        isLoadingThreads: function (newValue, oldValue) {
            if (newValue !== oldValue) {
                if (newValue == true) {
                    this.resetThreadForm();
                } else {
                    this.scrollThreadsToBottom();
                }
            }
        },
        selectedConversationId: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.expandedIndex = -1;
                    this.selectedCallHistory = [];
                    if (this.activeTab === FPTabsEnum.FamilyGroup) {
                        this.getFamCallhistory();
                    }
                }
            }
        }
    },

    created() {
        this.userOs = window.sessionStorage.getItem("userOs");
        this._familyPortalService = Container.instance.get(FamilyPortalService);
        this._messageService = Container.instance.get(MessageService);
        this.signalRService = Container.instance.get(SignalRService);
        this._permissionManager = Container.instance.get(PermissionManager);
        eventBus.on("dialogOpened", this.handleJitsiDialogOpened);
        eventBus.on("updateCallControls", this.handleCallControlsUpdate);
        eventBus.on("retryCall", this.handleRetryCall);
        eventBus.on("startJitiCall", this.handleStartJitsiCall);
        eventBus.on("updateDisableCallBtn", (response: boolean) => {
            this.disableCallBtns = response;
        });
    },

    async mounted() {
        this.callControls = {
            audioEnabled: true,
            videoEnabled: false
        };
        if (this.loggedInUserDetails) {
            this.currentUserDetails = {
                loginId: this.loggedInUserDetails.loginId,
                firstName: this.loggedInUserDetails.firstName,
                lastName: this.loggedInUserDetails.lastName
            };
        }
        document.addEventListener("click", this.handleCallHistoryOutsideClick);
    },
    beforeUnMount() {
        document.removeEventListener("click", this.handleCallHistoryOutsideClick);
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef.value)
        };
    },

    methods: {
        scrollThreadsToBottom() {
            setTimeout(() => {
                const container = this.$refs.fpConversationMain as HTMLElement;
                if (container) {
                    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
                }
            }, 300);
        },
        handleCallHistoryOutsideClick(event: MouseEvent) {
            const target = event.target as HTMLElement;

            if (!target.closest(".call-history-panel")) {
                this.expandedIndex = -1;
            }
        },
        toggleDropdown(calls: IConversationCall[], index: number) {
            if (this.expandedIndex === index) {
                this.expandedIndex = -1;
            } else {
                this.expandedIndex = index;
            }

            this.selectedCallHistory = calls;
        },

        handleCompose() {
            this.activeTab = FPTabsEnum.Thread;
            this.openCompose = !this.openCompose;
        },

        async getFamCallhistory(showLoading: boolean = true) {
            try {
                if (showLoading) {
                    this.isLoading = true;
                }

                const response = await this._familyPortalService.getFamilyContacts(this.selectedConversationId);
            } catch (e) {
                console.error(e);
                this.hasError = true;
            } finally {
                this.isLoading = false;
            }
        },

        async sendMessage(msg: any) {
            try {
                const query: ICreateThreadRequest = {
                    body: msg.message,
                    conversationGroupType: this.selectedConversation?.conversationGroupType,
                    patientId: this.conversationThreadsInfo.patientId,
                    subject: this.subject
                };
                const response: ICreateThreadResponse = await this._familyPortalService.createThread(query);
                response.conversationGroupType = this.selectedConversation?.conversationGroupType;
                eventBus.emit("createThread", response);
            } catch (error) {
                console.log(error, "error");
            }
        },

        updateSubject(value: string) {
            this.newThread.subject = value?.trim();
        },

        resetThreadForm() {
            this.newThread = {
                body: "",
                subject: ""
            };
            this.subject = "";
            this.openCompose = false;
            this.activeTab = FPTabsEnum.Thread;
        },

        toggleImportant() {
            // TODO: future implementation for important message
            console.log();
        },

        async handleActiveTab(tab: FPTabsEnum) {
            this.activeTab = tab;
            if (this.activeTab === FPTabsEnum.FamilyGroup) this.openCompose = false;
            await this.getFamCallhistory();
        },

        handleJitsiDialogOpened(isJitsiOpen: boolean) {
            this.dialogOpened = isJitsiOpen;
            if (!isJitsiOpen) {
                this.disableCallBtns = false;
            }
        },
        ...mapActions(useCallStore, ["setCurrentCallDetails", "setShowCallHeader", "setOutgoingCallInfo"]),
        generateRoomName() {
            return `${this.conversationThreadsInfo?.patientDetail?.firstName}-${this.conversationThreadsInfo?.patientDetail?.lastName}-${Date.now()}`;
        },

        async updateStatus(conversationId: string) {
            // update status when user gets a call while on current conv page
            if (conversationId === this.callStatus.conversationId) {
                if (this.callStatus.conferenceStatus === jitsiConferenceStatus.Ongoing) {
                    await this.getConversationRoomStatus();
                    this.setShowCallHeader({
                        convId: conversationId,
                        show: false,
                        roomId: this.callStatus.roomId,
                        callStartTime: new Date().toISOString()
                    });
                    this.startCallTimer(this.showCallHeader.callStartTime);
                } else {
                    clearInterval(this.intervalId);
                    this.setShowCallHeader({
                        convId: null,
                        show: false,
                        roomId: null,
                        callStartTime: null
                    });
                }
            }
        },

        async joinConversationRoom() {
            const isUserOnCall = this.currentCallDetails.isUserOnCall;
            this.callControls = {
                audioEnabled: false,
                videoEnabled: false
            };
            if (isUserOnCall) {
                const promptOptions: IPromptOptions = {
                    message: "Joining this call will disconnect your active call",
                    okText: "Join",
                    cancelText: "Cancel"
                };
                this.dialogRef = this.$dialog.open(Prompt, {
                    props: {
                        modal: true,
                        showHeader: false
                    },
                    data: { promptOptions },
                    onClose: async (confirm: any) => {
                        if (confirm.data) {
                            eventBus.emit("endCurrentCall");
                            this.setCurrentCallDetails({
                                convId: this.selectedConversationId,
                                roomId: this.showCallHeader.roomId,
                                isUserOnCall: true,
                                isFPCall: true
                            });
                            await this.handleJoinRoom(this.showCallHeader.roomId, false);
                        }
                    }
                });
            } else {
                this.setCurrentCallDetails({
                    convId: this.selectedConversationId,
                    roomId: this.showCallHeader.roomId,
                    isUserOnCall: true,
                    isFPCall: true
                });
                await this.handleJoinRoom(this.showCallHeader.roomId, false);
            }
        },

        async sendCallNotification(roomId: string, isRetryCall: boolean = false) {
            const callerName = `${this.currentUserDetails.firstName} ${this.currentUserDetails.lastName}`;
            let callMsg = `${callerName} is calling`;
            if (!isRetryCall) {
                this.setCurrentCallDetails({
                    convId: this.selectedConversationId,
                    roomId,
                    isUserOnCall: true,
                    isFPCall: true
                });
                callMsg = `${callerName} is calling`;
            }

            // Keep for future reference
            // const payload = {
            //     subjectId: this.currentUserDetails.loginId,
            //     callerName,
            //     callMessage: callMsg
            // };
            // await this._messageService.sendCallNotification(roomId, payload);

            if (this.outgoingCallInfo.convType == ConversationTypeEnum.GroupConversation) {
                await this.getConversationRoomStatus(false);
                await this.handleJoinRoom(roomId, false);
                this.startCallTimer(new Date().toISOString());
            } else {
                this.handleCalling(roomId, isRetryCall);
            }
        },

        handleCalling(roomId: string, isRetryCall: boolean = false) {
            let outgoingDetails: IOutgoingCallProps;
            if (isRetryCall) {
                outgoingDetails = {
                    recipient: this.outgoingCallInfo.users[0],
                    callType: this.outgoingCallInfo.roomType,
                    conversationId: this.outgoingCallInfo.convId,
                    roomId,
                    callControls: this.callControls,
                    isFamilyPortalCall: true
                };
                this.setOutgoingCallInfo({ ...this.outgoingCallInfo, isRetryCall: true });
            } else {
                outgoingDetails = {
                    recipient: this.selectedContact,
                    callType: this.selectedConversationType,
                    conversationId: this.selectedConversationId,
                    roomId,
                    callControls: this.callControls,
                    isFamilyPortalCall: true
                };
            }
            eventBus.emit("outgoingCallNotification", outgoingDetails);
        },

        async handleStartJitsiCall(callDetails: any) {
            const { roomId, isRetryCall, isFamilyPortalCall } = callDetails;
            if (!isFamilyPortalCall) {
                return;
            }

            await this.getConversationRoomStatus(isRetryCall);
            await this.handleJoinRoom(roomId, isRetryCall);
            this.setCurrentCallDetails({
                convId: this.outgoingCallInfo.convId,
                roomId,
                isUserOnCall: true,
                isFPCall: true
            });
            this.startCallTimer(new Date().toISOString());
            this.disableCallBtns = false;
        },

        startCallTimer(time: string) {
            clearInterval(this.intervalId);
            if (time && this.showCallHeader.show) {
                const callDate = moment.utc(time).local();
                this.intervalId = setInterval(() => {
                    const difference = moment(moment()).diff(moment(callDate), "seconds");
                    const duration = moment.duration(difference, "seconds");
                    this.callDuration =
                        difference >= 3600
                            ? moment.utc(duration.asMilliseconds()).format("HH:mm:ss")
                            : moment.utc(duration.asMilliseconds()).format("mm:ss");
                }, 1000);
            }
        },

        async getConversationRoomStatus(isRetryCall: boolean = false) {
            try {
                const conversationRoomStatus = await this._messageService.getConversationRoomStatus(
                    isRetryCall ? this.outgoingCallInfo.convId : this.selectedConversationId
                );
                this.conversationRoomDetails = conversationRoomStatus.roomDetails;
                this.setShowCallHeader({
                    convId: this.showCallHeader.convId,
                    show: false,
                    roomId: this.showCallHeader.roomId,
                    callStartTime: new Date()
                });
            } catch (error) {
                console.error("Error fetching conversation roomm status", error);
            }
        },

        async startMeeting(roomType: ConversationRoomTypeEnum, contact: IFamilyContacts, isRetryCall: boolean = false) {
            this.currentContact = contact;
            this.disableCallBtns = isRetryCall ? false : this.disableCallBtns;
            if (!this.disableCallBtns) {
                this.disableCallBtns = true;
                this.selectedConversationType = roomType;
                if (this.selectedConversationType == ConversationRoomTypeEnum.AudioCall) {
                    this.callControls = {
                        audioEnabled: true,
                        videoEnabled: false
                    };
                } else {
                    this.callControls = {
                        audioEnabled: true,
                        videoEnabled: true
                    };
                }
                if (!isRetryCall) {
                    this.setOutgoingCallInfo({
                        convId: this.selectedConversationId,
                        users: [this.currentContact],
                        convType: ConversationTypeEnum.SingleConversation,
                        roomType: roomType,
                        isRetryCall: false
                    });
                }
                const payload: IFPStartCallRequest = {
                    conversationGroupId: this.selectedConversationId,
                    recipientLoginId: contact?.loginId,
                    conversationRoomType: roomType
                };
                const startRoomResponse = await this._familyPortalService.startCall(payload);
                await this.sendCallNotification(startRoomResponse.roomId, isRetryCall);
            }
        },

        async handleJoinRoom(roomId: string, isRetryCall: boolean) {
            if (isRetryCall) {
                await jitsiRoomHandler(this, roomId, this.showCallHeader.callStartTime, this.callControls, {
                    users: this.outgoingCallInfo.users,
                    conversationRoomType: this.outgoingCallInfo.roomType
                });
            } else {
                await jitsiRoomHandler(this, roomId, this.showCallHeader.callStartTime, this.callControls, {
                    users: [this.selectedContact],
                    conversationRoomType: this.selectedConversationType
                });
            }
        },

        handleCallControlsUpdate(callControls: ICallControls) {
            this.callControls = callControls;
        },
        handleRetryCall() {
            this.startMeeting(this.outgoingCallInfo.roomType, this.currentContact, true);
        },

        getPermissionEnum(groupType: GroupTypeEnum) {
            let permissionEnum: ParentPermissionEnum = null;
            switch (groupType) {
                case GroupTypeEnum.Scheduling:
                    permissionEnum = ParentPermissionEnum.familyPortalScheduling;
                    break;
                case GroupTypeEnum.Billing:
                    permissionEnum = ParentPermissionEnum.familyPortalBilling;
                    break;
                case GroupTypeEnum.Clinical:
                    permissionEnum = ParentPermissionEnum.familyPortalClinical;
                    break;
                default:
                    permissionEnum = ParentPermissionEnum.familyPortalOthers;
                    break;
            }
            return permissionEnum;
        }
    },

    beforeUnmount() {
        clearInterval(this.intervalId);
        eventBus.off("dialogOpened", this.handleJitsiDialogOpened);
        eventBus.off("updateCallControls", this.handleCallControlsUpdate);
        eventBus.off("retryCall", this.handleRetryCall);
        eventBus.off("startJitiCall", this.handleStartJitsiCall);
    }
});
